import React, { useEffect, useState } from "react";

import { Alert, Snackbar } from "@mui/material";

import { ChatCircle, FileText, PuzzlePiece, X } from "@phosphor-icons/react";
import { Message } from "@twilio/conversations";

import { ActiveTab } from "@/components/Sidebar";
import useLesson from "@/contexts/useLesson";
import useConversation from "@/hooks/useConversation";
import useFirebaseContext from "@/hooks/useFirebaseAuth";
import { LunaTabName, UserRole } from "@/types";

interface SnackbarMessage {
  key: string;
  message: string;
  icon: React.ReactNode;
  tab: ActiveTab;
  subTab?: LunaTabName;
}

interface Props {
  handleNavigateToTab: (tab: ActiveTab, subTab?: LunaTabName) => void;
  activeTab: ActiveTab;
  isSidebarOpen: boolean;
}

const RealtimeSnackbar = ({
  handleNavigateToTab,
  activeTab,
  isSidebarOpen,
}: Props) => {
  const [snackPack, setSnackPack] = useState<SnackbarMessage[]>([]);
  const {
    lessonResourceState,
    lessonInteractionProfile,
    setLessonResourceState,
  } = useLesson();
  const { userRole } = useFirebaseContext();
  const { conversation, identity } = useConversation();

  const addSnackbar = (
    key: string,
    message: string,
    icon: React.ReactNode,
    tab: ActiveTab,
    subTab?: LunaTabName
  ) => {
    setSnackPack((prev) => [...prev, {
      key,
      message,
      icon,
      tab,
      subTab
    }]);
  };

  useEffect(() => {
    if (!lessonResourceState) return;

    // Initialize notifiedItems if it doesn't exist
    if (!lessonResourceState.notifiedItems) {
      setLessonResourceState({
        ...lessonResourceState,
        notifiedItems: [],
      });
      return;
    }

    const notifiedItems = lessonResourceState.notifiedItems;

    let problems = lessonInteractionProfile?.realtimeResources?.problems || [];
    let examples = lessonInteractionProfile?.realtimeResources?.examples || [];

    if (userRole === UserRole.Student) {
      problems = problems.filter((problem) => problem.isApproved);
      examples = examples.filter((example) => example.isApproved);
    }

    problems?.forEach((problem) => {
      if (!notifiedItems.includes(problem.id)) {
        addSnackbar(
          problem.id,
          "New problem!",
          <PuzzlePiece weight="bold" />,
          ActiveTab.Luna,
          LunaTabName.Problems
        );
        notifiedItems.push(problem.id);
        localStorage.setItem(
          lessonInteractionProfile?.id || "",
          JSON.stringify({
            ...lessonResourceState,
            notifiedItems
          })
        );
      }
    });

    examples?.forEach((example) => {
      if (!notifiedItems.includes(example.id)) {
        addSnackbar(
          example.id,
          "New example!",
          <FileText weight="bold" />,
          ActiveTab.Luna,
          LunaTabName.Examples
        );
        notifiedItems.push(example.id);
        localStorage.setItem(
          lessonInteractionProfile?.id || "",
          JSON.stringify({
            ...lessonResourceState,
            notifiedItems
          })
        );
      }
    });
  }, [lessonInteractionProfile?.realtimeResources, userRole]);

  const handleMessageAdded = (message: Message) => {
    if (
      message.author !== identity &&
      (!isSidebarOpen || activeTab !== ActiveTab.Chat)
    ) {
      addSnackbar(
        message.sid,
        "New message!",
        <ChatCircle weight="bold" />,
        ActiveTab.Chat
      );
    }
  };

  useEffect(() => {
    if (conversation) {
      conversation.on("messageAdded", handleMessageAdded);

      return () => {
        conversation.off("messageAdded", handleMessageAdded);
      };
    }
  }, [conversation, activeTab, identity, isSidebarOpen]);

  const handleClose = (key: string) => {
    setSnackPack((prev) => prev?.filter((snack) => snack.key !== key));
  };

  return (
    <>
      {snackPack?.map((snack, index) => (
        <Snackbar
          key={snack.key}
          open={true}
          autoHideDuration={10000}
          style={{
            bottom: `${index * 60 + 32}px`,
            transition: "bottom 0.3s ease-in-out",
          }}
          onClose={(event, reason) => {
            event?.stopPropagation();
            if (reason === "clickaway") {
              return;
            }
            handleClose(snack.key);
          }}
        >
          <Alert
            onClose={(event) => {
              event?.stopPropagation();
              handleClose(snack.key);
            }}
            sx={{
              cursor: "pointer",
            }}
            severity="info"
            icon={snack.icon}
            onClick={() => {
              handleNavigateToTab(snack.tab, snack.subTab);
              handleClose(snack.key);
            }}
            slots={{
              closeIcon: X
            }}
            slotProps={{
              closeButton: {
                sx: {
                  p: 0.5,
                  color: 'inherit'
                }
              }
            }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      ))}
    </>
  );
};

export default RealtimeSnackbar;
